import { AppLayoutHeaderWrapper } from "@src/containers/redesign/AppLayout/Header/HeaderWrapper";
import { AppLayoutInnerWrapper } from "@src/appV2/redesign/AppLayout/InnerWrapper";
import { NavigationMenuItem } from "@src/appV2/redesign/AppLayout/Navigation/MenuItem";
import { NavigationMenuItemsWrapper } from "@src/appV2/redesign/AppLayout/Navigation/MenuItemsWrapper";
import { NavigationMenuSectionTitle } from "@src/appV2/redesign/AppLayout/Navigation/MenuSectionTitle";
import { AppLayoutPageWrapper } from "@src/appV2/redesign/AppLayout/PageWrapper";
import { AppLayoutWrapper } from "@src/appV2/redesign/AppLayout/Wrapper";
import { logout } from "@src/modules/session";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { Modal } from "antd";
import firebase from "firebase/app";
import { useCallback, useEffect, useState } from "react";
import { Redirect, Route, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { SessionType } from "@src/modules/interface";
import { useInitializeLaunchDarkly } from "@src/hooks/useInitializeLaunchDarkly";
import { initBraze } from "@src/utils/braze";
import qs from "query-string";

import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { AdminNavigationMenuItemsContainer } from "./AdminNavigationMenuItemsContainer";
import { WorkerNavigationMenuItemsContainer } from "./WorkerNavigationMenuItemsContainer";
import { WorkplaceNavigationMenuItemsContainer } from "./WorkplaceNavigationMenuItemsContainer";
import { AdminMenuPriviligesProvider } from "./priviligesProviders/AdminMenuPriviligesProvider";
import { WorkplaceMenuPriviligesProvider } from "./priviligesProviders/WorkplaceMenuPriviligesProvider";
import { AdminRoutes } from "./AdminRoutes";
import { WorkplaceRoutes } from "./WorkplaceRoutes";
import { AppLayoutHeader } from "@src/containers/redesign/AppLayout/Header/AppLayoutHeader";
import { signInWithCustomToken } from "@src/containers/enter/firebase";

interface NavigationMenuContainerProps {
  isAdmin: boolean;
  isAgent: boolean;
  isFacility: boolean;
  session: any;
  location: any;
  get: () => void;
  getAdminProfile: () => void;
  loadingAdminProfile: boolean;
  initFirebaseSession: () => Promise<void>;
  getLocations: () => void;
  getEmployees: () => void;
}

const AdminAppLogin = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.AdminAppLogin,
    }))
);

const FirebaseLoginVerify = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.FirebaseLoginVerify,
    }))
);

function logoutConfirm() {
  Modal.confirm({
    title: "Are you sure you want to logout?",
    onOk: logout,
  });
}

export function NavigationMenuContainer(props: NavigationMenuContainerProps): JSX.Element {
  const {
    isAdmin,
    isAgent,
    isFacility: isWorkplace,
    session,
    location,
    get,
    getAdminProfile,
    loadingAdminProfile,
    initFirebaseSession,
    getLocations,
    getEmployees,
  } = props;

  const [loading, setLoading] = useState(true);
  const { ready, profile } = useSelector((state: SessionType) => state.session || {});
  const isAuthorized = isAdmin || isAgent || isWorkplace;

  const history = useHistory();

  useInitializeLaunchDarkly({ profile, isAdmin });

  // Initialize admin profile
  useEffect(() => {
    if (loadingAdminProfile) {
      getAdminProfile();
    }
  }, [loadingAdminProfile, getAdminProfile]);

  // Initialize Firebase session
  useEffect(() => {
    async function load() {
      setLoading(true);
      await initFirebaseSession();
      setLoading(false);
    }
    load();
  }, [initFirebaseSession]);

  // Initialize Braze (non-blocking, runs in background)
  useEffect(() => {
    if (session?.user?._id && !session.admin) {
      async function load() {
        await initBraze(session?.user?._id);
      }
      load();
    }
  }, [session?.admin, session?.user?._id]);

  // Fetch session data and locations
  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (!isAuthorized) {
      return;
    }
    get();
    getLocations();

    if (session?.admin) {
      getEmployees();
    }
  }, [isAuthorized, get, getLocations, getEmployees, loading, ready, session?.admin]);

  const qString = qs.parse(location.search);
  const [isToken, setToken] = useState<any>(qString.token);
  const checkForAccessToken = useCallback(async (token) => {
    const result = await signInWithCustomToken(token);
    if ("error" in result) {
      setToken(false);
    } else {
      setToken(true);
    }
  }, []);

  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (isToken) {
      checkForAccessToken(isToken);
    }
  }, [checkForAccessToken, loading, ready]);

  // redirect to the login page, if the user is not authenticated and not use a verification link
  useEffect(() => {
    const currentPath = window.location.pathname;

    return firebase.auth().onAuthStateChanged((user) => {
      // Skip redirect during the email verification process
      const isVerificationPage = currentPath.includes("login/verify");

      if (!user && !isVerificationPage) {
        history.replace("/login");
      }
    });
  }, [history]);

  const renderNavigationMenu = () => {
    if (isAdmin) {
      return <AdminNavigationMenuItemsContainer />;
    }

    if (isAgent) {
      return <WorkerNavigationMenuItemsContainer />;
    }

    if (isWorkplace) {
      return <WorkplaceNavigationMenuItemsContainer />;
    }

    return null;
  };

  function renderStartPage() {
    if (isAdmin) {
      return <Redirect to={routes.admin} />;
    }

    if (isAgent) {
      return <Redirect to={routes.agentDashboard} />;
    }

    if (isWorkplace) {
      return <Redirect to={routes.agentDashboard} />;
    }

    return null;
  }

  // Early return for login routes - these should always render
  const isLoginRoute =
    location.pathname === routes.login || location.pathname === routes.loginVerify;

  if (isLoginRoute) {
    return (
      <BerlinThemeProvider>
        <Route exact path={routes.login} component={AdminAppLogin} />
        <Route exact path={routes.loginVerify} component={FirebaseLoginVerify} />
      </BerlinThemeProvider>
    );
  }

  // Guard to prevent rendering while session is initializing
  // This matches the old AppLayout logic (line 918 in appLayout.tsx)
  // Prevents API calls from firing before auth token and profile data are ready
  //
  // Important: ready becomes true BEFORE fetchSession() completes loading the full profile
  // We need to wait for profile.tmz to exist as it's required by API calls in AgentDashboard
  const hasRequiredProfileData = profile && profile.userId && profile.tmz;

  if ((loading || !ready) && session.userId) {
    return <></>;
  }

  // Additional guard: ensure profile data is fully loaded before rendering
  // AgentDashboard needs profile.tmz, profile.geoLocation, profile.preference, etc.
  if (!hasRequiredProfileData) {
    return <></>;
  }

  return (
    <BerlinThemeProvider>
      <AdminMenuPriviligesProvider>
        <WorkplaceMenuPriviligesProvider isWorkplace={isWorkplace}>
          <AppLayoutWrapper>
            <AppLayoutHeaderWrapper>
              <AppLayoutHeader />
            </AppLayoutHeaderWrapper>
            <AppLayoutInnerWrapper>
              <NavigationMenuItemsWrapper>
                {renderNavigationMenu()}
                <NavigationMenuSectionTitle>Logout</NavigationMenuSectionTitle>
                <NavigationMenuItem
                  iconType="door-straight"
                  isSelected={false}
                  onClick={() => {
                    logoutConfirm();
                  }}
                >
                  Logout
                </NavigationMenuItem>
              </NavigationMenuItemsWrapper>
              <AppLayoutPageWrapper>
                <Route exact path="/" render={() => renderStartPage()} />

                <Route exact path="/enter" render={() => renderStartPage()} />

                <AdminRoutes />

                <WorkplaceRoutes isAgent={isAgent} />
              </AppLayoutPageWrapper>
            </AppLayoutInnerWrapper>
          </AppLayoutWrapper>
        </WorkplaceMenuPriviligesProvider>
      </AdminMenuPriviligesProvider>
    </BerlinThemeProvider>
  );
}
